<template>
  <div v-if="customer" class="py-6">
    <template v-if="customer">
      <div class="flex justify-between items-center space-x-8 mb-6 border-b pb-4">
        <div>
          <h4 class="text-lg mr-4 leading-none font-semibold ">
            {{ $t('components.customer.profile.giftCards.balances') }}
          </h4>
        </div>
      </div>

      <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white dark:bg-gray-800/20 dark:border dark:border-gray-800 shadow-lg rounded-2xl mb-12">
        <div class="w-full flex p-4 items-center justify-between flex-grow">
          <div>
            <p class="opacity-60 text-sm">
              {{ $t('general.creditBalance') }}
            </p>

            <p class="text-lg font-semibold">
              {{ customer.balances.creditLabel }}
            </p>
          </div>

          <div class="flex items-center space-x-1">
            <o-tooltip v-if="!customer.isArchived" :text="$t('components.customer.profile.giftCards.updateCreditBalance')">
              <o-button flat size="sm" icon="edit" @click="handleUpdateCreditBalance" />
            </o-tooltip>

            <o-panel-link
              tag="o-button"
              :path="`/customer/credit/${customer.idCustomer}`"
              flat
              size="sm"
              icon="view"
              :icon-size="11"
              class="ml-2"
            />
          </div>
        </div>

        <div v-if="customer.balances.loyaltyPointsInCurrencyLabel && $features.get('loyalty')" class="w-full flex p-4 items-center justify-between flex-grow">
          <div>
            <p class="opacity-60 text-sm">
              {{ $t('general.loyaltyPoints') }}
            </p>

            <p class="text-lg font-semibold">
              {{ customer.balances.loyaltyPoints || 0 }} <span v-if="customer.balances.loyaltyPoints">({{ $currency.format(customer.balances.loyaltyPointsInCurrency || 0) }})</span>
            </p>
          </div>

          <div class="flex items-center space-x-1">
            <o-tooltip :text="$t('components.customer.actions.loyaltyPointBalance.title')">
              <o-button flat size="sm" icon="edit" @click="handleUpdateLoyaltyPointBalance" />
            </o-tooltip>

            <o-panel-link
              tag="o-button"
              :path="`/customer/loyalty/${customer.idCustomer}`"
              flat
              size="sm"
              icon="view"
              :icon-size="11"
              class="ml-2"
            />
          </div>
        </div>

        <div v-if="$features.get('gift-cards')" class="w-full flex p-4 items-center justify-between flex-grow">
          <div>
            <p class="opacity-60 text-sm">
              {{ $t('components.customer.profile.giftCards.giftCardBalance') }}
            </p>

            <p class="text-lg font-semibold">
              {{ customer.balances.giftCardBalanceLabel }}
            </p>
          </div>
        </div>
      </div>
    </template>

    <template v-if="$features.get('gift-cards') && giftCards">
      <div class="flex justify-between items-center space-x-8 mb-8 border-b pb-4">
        <div>
          <h4 class="text-lg mr-4 leading-none font-semibold">
            {{ $t('general.giftCards') }}
          </h4>
        </div>

        <div class="flex items-center space-x-2 flex-shrink-0">
          <o-button
            variant="primary"
            flat
            @click="handleAdd"
          >
            {{ $t('general.add') }}
          </o-button>
        </div>
      </div>
      <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full dark:bg-gray-800/20 dark:border dark:border-gray-800 shadow-lg rounded-2xl mb-4">
        <p v-if="giftCards.length === 0" class="p-4 text-sm text-gray-600 dark:text-gray-300">
          {{ $t('components.customer.profile.giftCards.empty') }}
        </p>
        <gift-card
          v-for="giftCard in giftCards"
          :key="giftCard.idGiftCard"
          :giftCard="giftCard"
          class="w-full flex justify-between items-center flex-grow p-4"
          @email="handleEmail(giftCard)"
          @delete="handleDelete(giftCard)"
          @print="handlePrint(giftCard)"
          @createOrder="handleCreateOrder(giftCard)"
        />
      </div>
    </template>

    <add-dialog
      ref="addDialog"
      @success="fetchGiftCards"
    />

    <email-dialog
      ref="emailDialog"
      @success="fetchGiftCards"
    />

    <delete-dialog
      ref="deleteDialog"
      @success="fetchGiftCards"
    />

    <loyalty-point-balance-dialog
      ref="loyaltyPointBalanceDialog"
      :customer="customer"
    />

    <credit-balance-dialog
      ref="creditBalanceDialog"
      :customer="customer"
    />

    <create-order-dialog
      ref="createOrderDialog"
    />
  </div>
</template>

<script setup lang="ts">
import GiftCard from './giftCard.vue'
import AddDialog from '@/components/giftCard/actions/Add.vue'
import EmailDialog from '@/components/giftCard/actions/Email.vue'
import DeleteDialog from '@/components/giftCard/actions/Delete.vue'
import LoyaltyPointBalanceDialog from '@/components/customer/actions/LoyaltyPointBalance.vue'
import CreditBalanceDialog from '@/components/customer/actions/CreditBalance.vue'
import CreateOrderDialog from '@/components/order/actions/CreateOrder.vue'

interface Props {
  customer?: Record<string, any> | null
}

const props = withDefaults(defineProps<Props>(), {
  customer: null,
})

const { $api, $bus, $features } = useNuxtApp()

const addDialog = ref<any>()
const emailDialog = ref<any>()
const deleteDialog = ref<any>()
const loyaltyPointBalanceDialog = ref<any>()
const creditBalanceDialog = ref<any>()
const createOrderDialog = ref<any>()

const giftCards = ref<any[] | null>(null)
const loading = ref(false)

const fetchGiftCards = async () => {
  if (($features as any).get('gift-cards')) {
    loading.value = true

    try {
      const result = await $api.customer.giftCards(String(props.customer!.idCustomer))
      giftCards.value = result.items
    } catch (e) {
      console.log(e)
    }

    loading.value = false
  }
}

const handleAdd = () => {
  const customer = props.customer
  addDialog.value.open({ customer })
}

const handleDelete = (giftCard: any) => {
  deleteDialog.value.open(giftCard)
}

const handleEmail = (giftCard: any) => {
  emailDialog.value.open(giftCard)
}

const handlePrint = (giftCard: any) => {
  // Note: printDialog ref not defined in template - may need adding
}

const handleUpdateLoyaltyPointBalance = () => {
  loyaltyPointBalanceDialog.value.open()
}

const handleUpdateCreditBalance = () => {
  creditBalanceDialog.value.open()
}

const handleCreateOrder = (giftCard: any) => {
  const customer = giftCard.customer
  const date = new Date()
  const items = [giftCard]

  const data = {
    customer,
    date,
    items,
  }

  createOrderDialog.value.open(data)
}

// created
const uid = getCurrentInstance()?.uid
$bus.on.all('GiftCard', uid, (giftCard: any) => {
  const idCustomer = props.customer?.idCustomer
  const current = idCustomer === (giftCard?.customer?.idCustomer || giftCard?.customer?.id)

  if (current) {
    fetchGiftCards()
  }
})

onMounted(() => {
  fetchGiftCards()
})

onBeforeUnmount(() => {
  $bus.off.all(uid)
})
</script>
